/**
 * `ScoreBadge` — visualizzazione compatta di un voto `0..10` come **numero a una
 * cifra decimale** dentro un box colorato secondo la scala rosso→verde
 * (`scoreColor`, con `6` come soglia di sufficienza).
 *
 * È la resa alternativa alle stelline (`ScoreStars`) per le colonne di sintesi
 * (es. "Score" nella dashboard "Livelli"): mette in evidenza il valore numerico
 * e ne comunica il livello con il colore di sfondo, coerente con il resto
 * dell'app. Il voto `0` è valido (box colorato al minimo della scala); per
 * l'assenza (`null`) usare l'indicatore "—", non questo componente.
 */
import { scoreColor } from './StarRating';

export interface ScoreBadgeProps {
  /** Voto sulla scala `0..10` (può essere decimale, es. una media). */
  value: number;
  /** Etichetta accessibile (es. "Score"). */
  label: string;
}

export function ScoreBadge({ value, label }: ScoreBadgeProps) {
  // Score oltre il massimo della scala (bonus certificazioni): trattamento oro
  // con etichetta "10+", a prescindere dal colore della scala rosso→verde.
  const isOverMax = value > 10;
  // Colore di sfondo dalla scala rosso→verde. Per voti non positivi (es. `0`)
  // `scoreColor` restituisce `null`: usiamo un box neutro con testo scuro,
  // altrimenti il testo bianco su sfondo colorato garantisce contrasto.
  const color = scoreColor(value);
  const rounded = value.toFixed(1);

  if (isOverMax) {
    return (
      <span
        className="score-badge score-badge--gold"
        aria-label={`${label}: oltre 10 (${rounded})`}
        title={`${label}: ${rounded}`}
      >
        10+
      </span>
    );
  }

  return (
    <span
      className={['score-badge', color === null ? 'score-badge--muted' : '']
        .filter(Boolean)
        .join(' ')}
      style={color === null ? undefined : { background: color }}
      aria-label={`${label}: ${rounded} su 10`}
    >
      {rounded}
    </span>
  );
}

export default ScoreBadge;
